import ReactECharts from "echarts-for-react";

function formatNumber(value) {
  if (value == null || isNaN(value)) return 'N/A';
  return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function RatioTrendChart({
  title,
  valueLabel,
  timeseries,
  metricKey,
  range,
  onRangeChange,
  currentValue,
  lineColor,
  areaColorStart,
  areaColorEnd,
  currentColorClass,
  height = "220px",
}) {
  const allTs = timeseries || [];

  if (allTs.length === 0) {
    return (
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-gray-100 text-xs font-semibold tracking-wider">{title}</span>
        </div>
        <p className="text-sm font-bold text-gray-400">N/A</p>
      </div>
    );
  }

  const yearsBack = range === "3Y" ? 3 : range === "5Y" ? 5 : 10;
  const cutoff = new Date();
  cutoff.setFullYear(cutoff.getFullYear() - yearsBack);
  const cutoffStr = `${cutoff.getFullYear()}-${String(cutoff.getMonth() + 1).padStart(2, '0')}`;
  const filtered = allTs.filter(d => d.date >= cutoffStr);
  const displayTs = filtered.length > 0 ? filtered : allTs;
  const validVals = displayTs.filter(d => d[metricKey] != null).map(d => d[metricKey]);
  const periodAvg = validVals.length > 0
    ? Math.round((validVals.reduce((a, b) => a + b, 0) / validVals.length) * 10) / 10
    : null;
  const xLabels = displayTs.map(d => d.date.substring(0, 4));
  const labelInterval = Math.max(0, Math.floor(displayTs.length / 5) - 1);

  const option = {
    backgroundColor: 'transparent',
    grid: { top: 16, right: 8, bottom: 38, left: 36, containLabel: false },
    xAxis: {
      type: 'category',
      data: xLabels,
      axisLabel: {
        color: '#6b7280',
        fontSize: 9,
        interval: labelInterval,
        formatter: (val, idx) => {
          if (idx === 0 || xLabels[idx] !== xLabels[idx - 1]) return val;
          return '';
        },
      },
      axisLine: { lineStyle: { color: '#374151' } },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      axisLabel: { color: '#6b7280', fontSize: 9 },
      splitLine: { lineStyle: { color: '#1f2937', type: 'dashed' } },
      axisLine: { show: false },
    },
    series: [
      {
        name: valueLabel,
        type: 'line',
        data: displayTs.map(d => d[metricKey]),
        smooth: true,
        symbol: 'none',
        lineStyle: { color: lineColor, width: 1.5 },
        areaStyle: {
          color: {
            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: areaColorStart },
              { offset: 1, color: areaColorEnd },
            ],
          },
        },
      },
      {
        name: `Avg (${range})`,
        type: 'line',
        data: displayTs.map(() => periodAvg),
        symbol: 'none',
        lineStyle: { color: '#9ca3af', width: 1, type: 'dashed' },
      },
    ],
    tooltip: {
      trigger: 'axis',
      backgroundColor: '#111827',
      borderColor: '#374151',
      textStyle: { color: '#e5e7eb', fontSize: 11 },
      formatter: (params) => {
        const date = params[0]?.axisValue || '';
        const val = params[0]?.value != null ? `${valueLabel}: <b>${params[0].value}</b>` : '';
        const avg = params[1]?.value != null ? `${range} Avg: ${params[1].value}` : '';
        return `${date}<br/>${val}${avg ? '<br/>' + avg : ''}`;
      },
    },
  };

  return (
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-gray-100 text-xs font-semibold tracking-wider">{title}</span>
        <div className="flex items-center gap-0.5 bg-gray-900/60 rounded p-0.5">
          {["3Y", "5Y", "10Y"].map(r => (
            <button
              key={r}
              onClick={() => onRangeChange(r)}
              className={`px-1.5 py-0.5 text-[9px] font-semibold rounded transition-colors ${range === r ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-gray-200'}`}
            >
              {r}
            </button>
          ))}
        </div>
      </div>
      <ReactECharts style={{ height, width: '100%' }} option={option} />
      <div className="flex gap-4 mt-1 justify-center">
        <span className="text-[10px] text-gray-500">{range} Avg: <span className="text-gray-300 font-semibold">{periodAvg ?? 'N/A'}</span></span>
        {currentValue != null && (
          <span className="text-[10px] text-gray-500">
            Current: <span className={`${currentColorClass} font-semibold`}>{formatNumber(currentValue)}</span>
          </span>
        )}
      </div>
    </div>
  );
}
